// Thin client around the `manage-review-invitation` edge function for the
// unified Invite (collaborator + review) feature.
//
// Why not direct table access?
//   The edge function enforces server-side caps, suppression checks, email
//   binding, and audit fields. Bypassing it would re-create those rules in
//   the client and is a security smell. RLS on `review_requests` gives the
//   requestor SELECT/UPDATE on rows they own — but creation, accept, revoke,
//   resend, and intent-changes all run through the function so we have a
//   single source of truth for invariants.
//
// Errors: the function returns `{ error: string }` with HTTP 4xx/5xx; we
// translate that into thrown Error objects so React Query's `onError` works.

import { supabase } from '@/lib/supabase';

export type InvitationIntent = 'collaborate' | 'review';

export interface CollaboratorRow {
  id: string;
  paper_id: string;
  version_id: string | null;
  invitee_email: string | null;
  personal_note: string | null;
  status: string; // 'pending' | 'accepted' | 'declined' | 'expired'
  intent: InvitationIntent;
  revoked_at: string | null;
  created_at: string;
  sent_at: string | null;
  last_sent_at: string | null;
  delivered_at: string | null;
  expires_at: string | null;
  resend_count: number;
  last_error: string | null;
}

export interface SharedWithMeRow {
  id: string;
  paper_id: string;
  paper_title: string | null;
  owner_name: string | null;
  intent: InvitationIntent;
  personal_note: string | null;
  created_at: string;
  expires_at: string | null;
}

export interface CreateInvitePayload {
  paperId: string;
  versionId?: string | null;
  intent: InvitationIntent;
  invitees: Array<{ email: string; personalNote?: string | null }>;
  warrantyAcknowledged?: boolean;
}

async function invoke<T>(action: string, body: Record<string, unknown> = {}): Promise<T> {
  const { data, error } = await supabase.functions.invoke('manage-review-invitation', {
    body: { action, ...body },
  });
  if (error) {
    // supabase.functions.invoke wraps non-2xx into a FunctionsHttpError whose
    // `context.response` holds the original body — try to surface that.
    const ctx = (error as { context?: { response?: Response } }).context;
    if (ctx?.response) {
      try {
        const parsed = await ctx.response.clone().json();
        const msg = parsed?.error || parsed?.message;
        if (msg) throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
      } catch {
        /* fall through */
      }
    }
    throw error;
  }
  if (data && typeof data === 'object' && 'error' in data && data.error) {
    throw new Error(String((data as { error: unknown }).error));
  }
  return data as T;
}

export interface InvitationQuota {
  paperOpenReviewUsed: number;
  paperOpenReviewLimit: number;
  paperCollabUsed: number;
  paperCollabLimit: number;
  monthlyUsed: number;
  monthlyLimit: number;
}

export const collaboratorsApi = {
  listForPaper: async (paperId: string): Promise<{ rows: CollaboratorRow[]; quota: InvitationQuota | null }> => {
    // Edge function returns `{ invitations, quota }`. Normalize to `rows`
    // so the React Query consumers (`useCollaborators`, `AccessList`) work
    // off a stable shape regardless of backend naming.
    const raw = await invoke<{ invitations?: CollaboratorRow[]; rows?: CollaboratorRow[]; quota?: InvitationQuota }>(
      'list',
      { paperId },
    );
    return {
      rows: raw.rows ?? raw.invitations ?? [],
      quota: raw.quota ?? null,
    };
  },
  listForUser: () =>
    invoke<{ rows: SharedWithMeRow[] }>('list_for_user'),
  create: (payload: CreateInvitePayload) =>
    invoke<{ created: number; results: Array<{ email: string; state: string }> }>(
      'create',
      payload as unknown as Record<string, unknown>,
    ),
  resend: (invitationId: string) =>
    invoke<{ ok: true }>('resend', { invitationId }),
  revoke: (invitationId: string) =>
    invoke<{ ok: true }>('revoke', { invitationId }),
  unRevoke: (invitationId: string) =>
    invoke<{ ok: true }>('un_revoke', { invitationId }),
  setIntent: (invitationId: string, intent: InvitationIntent) =>
    invoke<{ ok: true; unchanged?: boolean }>('set_intent', { invitationId, intent }),
};
